import React, { useMemo } from 'react';
import clsx from 'clsx';

import { Input } from '@/slw/shared/ui/input';
import { useDebounce } from '@/slw/shared/hooks/use-debounce';
import { Checkbox } from '@/slw/shared/ui/checkbox';
import { formatNumber } from '@/slw/shared/lib/format-number';

interface Option {
  label: string;
  value: string;
  count?: number;
}

interface FilterCheckboxGroupProps {
  label: string;
  options: Option[];
  value: string[];
  onChange: (val: string[]) => void;
  searchable?: boolean;
  className?: string;
  name?: string;
  onSelectAll?: () => void;
  onDeselectAll?: () => void;
  toggleableSelectAll?: boolean;
}

export function FilterCheckboxGroup({
  options,
  value,
  onChange,
  searchable = false,
  className,
  name,
  // onSelectAll,
  onDeselectAll,
  toggleableSelectAll = false,
}: FilterCheckboxGroupProps) {
  const [search, setSearch] = React.useState('');

  const searchValue = useDebounce(search, 500);

  const filtered = useMemo(
    () =>
      !searchValue ? options : options.filter((opt) => opt.label.toLowerCase().includes(searchValue.toLowerCase())),
    [searchValue, options],
  );

  const handleToggle = (val: string) => {
    if (value.includes(val)) {
      onChange(value.filter((v) => v !== val));
    } else {
      onChange([...value, val]);
    }
  };

  // const handleSelectAll = () => {
  //   onChange(filtered.map((v) => v.value));
  //   onSelectAll?.();
  // };

  const handleDeselectAll = () => {
    onChange([]);
    onDeselectAll?.();
  };

  return (
    <div className={clsx('max-h-50 overflow-hidden', className)}>
      {searchable && (
        <Input
          name={name}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder='Search'
          className='rounded-xl px-3 py-2 mb-2'
        />
      )}
      {toggleableSelectAll && (
        <div className='flex justify-end text-xs mb-2 text-[#C9A876]'>
          {/* <button type='button' className='text-sm cursor-pointer' onClick={() => handleSelectAll()}>
            Select all
          </button> */}
          <button type='button' className='text-sm cursor-pointer' onClick={() => handleDeselectAll()}>
            Deselect all
          </button>
        </div>
      )}
      <div className='flex flex-col gap-2 max-h-48 overflow-y-auto pr-1'>
        {filtered.map((opt, idx) => (
          <label key={`${opt.value}_${idx}`} className='flex items-center gap-3 cursor-pointer text-[15px]'>
            <Checkbox
              checked={value.includes(opt.value)}
              onCheckedChange={() => handleToggle(opt.value)}
              className='w-6 h-6 rounded-md'
              aria-checked={value.includes(opt.value)}
              aria-label={opt.label}
            />
            <span className='text-sm font-medium'>{opt.label}</span>
            {opt.count !== undefined && <span className='ml-auto text-sm font-medium'>{formatNumber(opt.count)}</span>}
          </label>
        ))}
        {filtered.length === 0 && <span className='text-sm'>No options found</span>}
      </div>
    </div>
  );
}
